/* global Vue, VueI18n, LOYALTY_API, LOCALE, PREFIX, VeeValidate */

import { AuthService, fetchWithTokenAsync } from '../../AuthService'
import CountriesDropdown from '../../apps/components/CountriesDropdown'
import PhoneNumber from '../../apps/components/PhoneNumber'
import '../../validation-rules'

import { messagesCommon } from '../../i18n/translations-common'
import { setErrorAndStopLoading, setSuccessAndStopLoading, startLoading, stopLoading } from '../../apps/components/Notifications'
const m = messagesCommon[LOCALE].loyaltyProfile

const LoyaltyProfile = {
  name: 'LoyaltyProfile',
  components: { CountriesDropdown, PhoneNumber },
  template: `
<div class="container grid no-padding-sm-down">
  <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2 loyalty-form__wrapper">
    <form class="loyalty-form" ref="formElement">
      <div class="loyalty-my-profile__identity loyalty-form__border">
        <h2 class="h4 uppercase no-margin">{{ $t('loyaltyProfile.identity') }}</h2>
        <hr>

        <label class="form-element">
          {{ $t('loyaltyProfile.title') }} *
          <select class="select-input" id="title" name="title" v-model="form.title" @blur="validateField('title')">
            <option value="mr">{{ $t('loyaltyProfile.profileTitle.mr') }}</option>
            <option value="ms">{{ $t('loyaltyProfile.profileTitle.mrs') }}</option>
          </select>
          <span class="field-error">{{ formErrors.title }}</span>
        </label>

        <label class="form-element">
          {{ $t('loyaltyProfile.firstName') }} *
          <input id="firstName" name="firstName" type="text" v-model="form.firstName" @blur="validateField('firstName')">
          <span class="field-error">{{ formErrors.firstName }}</span>
        </label>

        <label class="form-element">
          {{ $t('loyaltyProfile.lastName') }} *
          <input id="lastName" name="lastName" type="text" v-model="form.lastName" @blur="validateField('lastName')">
          <span class="field-error">{{ formErrors.lastName }}</span>
        </label>

        <label class="form-element">
          {{ $t('loyaltyProfile.dob') }} *
          <input id="birthdate" name="birthdate" type="date" v-model="form.birthdate" :max="new Date().toISOString().substring(0,10)" @blur="validateField('birthdate')">
          <span class="field-error">{{ formErrors.birthdate }}</span>
        </label>

        <label v-if="m" class="form-element">
          {{ $t('loyaltyProfile.locale') }}
          <select class="select-input" id="locale" name="locale" v-model="form.locale">
            <option v-for="l in profileLocales" :value="l">{{ m.profileLocales['l' + l] }}</option>
          </select>
        </label>

        <label class="label-checkbox">
          <input id="isMaiFamily" name="isMaiFamily" type="checkbox" v-model="form.isMaiFamily" :disabled="isMaiFamilyLocked">
          <span>{{ $t('loyaltyProfile.isMaiFamily') }}</span>
        </label>

        <label v-if="form.isMaiFamily" class="form-element">
          {{ $t('loyaltyProfile.corporateCardNumber') }} *
          <input id="corporateCardNumber" name="corporateCardNumber" type="text" v-model="form.corporateCardNumber" :disabled="isMaiFamilyLocked" @blur="validateField('corporateCardNumber')">
          <span class="field-error">{{ formErrors.corporateCardNumber }}</span>
        </label>

        <label v-if="form.isMaiFamily" class="form-element">
          {{ $t('loyaltyProfile.companyName') }} *
          <select class="select-input" id="companyName" name="companyName" v-model="form.companyName" :disabled="isMaiFamilyLocked" @blur="validateField('companyName')">
            <option v-for="company in maiFamilyCompanies" :value="company">{{ company }}</option>
          </select>
          <span class="field-error">{{ formErrors.companyName }}</span>
        </label>

        <div v-if="form.isMaiFamily" class="note" v-html="m.maiFamilyNote"></div>
      </div>

      <div class="loyalty-my-profile__contact loyalty-form__border">
        <h2 class="h4 no-margin">{{ $t('loyaltyProfile.contact') }}</h2>
        <hr>

        <label class="form-element">
          {{ $t('loyaltyProfile.email') }}
          <input id="profileEmail" type="email" disabled v-model="form.email">
        </label>

        <p>{{ $t('myProfile.changeEmailText') }} <a href="mailto:Loyalty@maistra.hr" target="_blank">{{ $t('myProfile.changeEmailCC') }}</a>.</p>

        <label class="form-element">
          {{ $t('loyaltyProfile.address') }}
          <input id="address" name="address" type="text" v-model="form.address">
        </label>

        <label class="form-element">
          {{ $t('loyaltyProfile.postalCode') }}
          <input id="postalCode" name="postalCode" type="text" v-model="form.postalCode">
        </label>

        <label class="form-element">
          {{ $t('loyaltyProfile.city') }} *
          <input id="city" name="city" type="text" v-model="form.city" @blur="validateField('city')">
          <span class="field-error">{{ formErrors.city }}</span>
        </label>

        <div class="form-element">
          <CountriesDropdown :countryCode="form.country" id="countryDropdown" @countryChanged="form.country = $event.code_3; countryPrefix = $event.phone_code" :label="$t('country') + ' *'" />
          <input id="country" type="hidden" v-model="form.country">
          <span class="field-error">{{ formErrors.country }}</span>
        </div>

        <div class="form-element">
          <PhoneNumber :countryPrefix="countryPrefix" :phoneNumber="form.phonenumber" id="phonenumberField" @phoneNumberChanged="form.phonenumber = $event" :label="$t('loyaltyProfile.phoneNumber') + ' *'" @blur="validateField('phonenumber')" />
          <input id="phonenumber" type="hidden" v-model="form.phonenumber">
          <span class="field-error">{{ formErrors.phonenumber }}</span>
        </div>

        <p>{{ $t('myProfile.newsletter') }}</p>

        <p class="no-margin-bottom">* {{ $t('myProfile.requiredFields') }}</p>
      </div>
    </form>

    <div class="button-group" v-show="!isSubmitting">
      <button class="button button--outline" @click="$router.push('/')">{{ $t('cancel') }}</button>
      <button class="button button--maistar" @click="saveProfile()">{{ $t('confirm') }}</button>
    </div>
  </div>
</div>
`,
  emits: ['success'],
  props: {
    profile: {
      type: Object,
      default: {},
    },
  },
  setup (props, { emit }) {
    const { t } = VueI18n.useI18n()
    const form = Vue.reactive({})
    const formErrors = Vue.reactive({})
    const countryPrefix = Vue.ref('')
    const formElement = Vue.ref(null)
    const isMaiFamilyLocked = Vue.ref(false)

    const requiredIfMaiFamily = value => !form.isMaiFamily || value?.length > 0 || messagesCommon[LOCALE].formValidation.required
    const validationSchema = {
      title: 'required',
      firstName: 'required|min:1',
      lastName: 'required|min:1',
      birthdate: 'required|birthdate',
      city: 'required',
      country: 'required',
      phonenumber: 'required|phone',
      corporateCardNumber: requiredIfMaiFamily,
      companyName: requiredIfMaiFamily,
    }
    const profileLocales = ['1050', '1040', '1033', '1031', '1060']
    const maiFamilyCompanies = ['Adris Group', 'Maistra Hospitality Group', 'Cromaris', 'Croatia osiguranje', 'Abilia']

    const { handleSubmit, errors, isSubmitting } = VeeValidate.useForm({
      validationSchema,
      initialValues: form,
    })
    Vue.watch(isSubmitting, () => {
      if (Object.keys(errors.value).length && formElement.value) {
        const highlight = Array.from(formElement.value.querySelectorAll('.field-error')).filter(er => er.innerHTML)[0] || null
        if (highlight) { highlight.parentNode.scrollIntoView({ behavior: 'smooth' }) }
      }
    })

    Vue.watch(props.profile, (currentValue) => {
      Object.assign(form, currentValue)
      isMaiFamilyLocked.value = currentValue?.isMaiFamily === true
    }, { immediate: true, deep: true })

    Vue.watch(() => form.isMaiFamily, (isMaiFamily) => {
      if (!isMaiFamily) {
        form.corporateCardNumber = ''
        form.companyName = ''
        fieldRefs.corporateCardNumber.setErrors([])
        fieldRefs.companyName.setErrors([])
      }
    })

    const fieldRefs = Vue.reactive({})
    Object.keys(validationSchema).forEach((field) => {
      const fieldObj = VeeValidate.useField(field, undefined, {
        validateOnValueUpdate: false,
      })

      form[field] = fieldObj.value
      formErrors[field] = fieldObj.errorMessage
      fieldRefs[field] = fieldObj
    })

    function validateField (field) {
      fieldRefs[field].validate()
    }

    const saveProfile = handleSubmit(async () => {
      startLoading('profile-update')
      form.phonenumber = form.phonenumber.replace(/[()\-\s]/g, '')

      try {
        const dmdUniqueUserId = window.localStorage.getItem('DMD_uniqueUserId')
        if (dmdUniqueUserId) {
          form.uniqueId = dmdUniqueUserId
        }

        const responseObj = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/loyaltyprofiles/upsert`,
          AuthService.accessToken, 'POST', form)

        if (parseInt(responseObj?.value) > 0) {
          const successPayload = { title: t('loyaltyProfile.updateSuccess') }
          setSuccessAndStopLoading(successPayload)
          emit('success', successPayload)
          if (form.isMaiFamily) {
            isMaiFamilyLocked.value = true
          }
          window.localStorage.removeItem('DMD_uniqueUserId')
          AuthService.fetchAndSaveUserData()
        } else if (responseObj?.message) {
          throw new Error(responseObj?.message)
        } else {
          throw new Error('update loyalty profile failed')
        }

        stopLoading('profile-update')
      } catch (e) {
        setErrorAndStopLoading(e)
      }
    })

    return { form, formErrors, profileLocales, maiFamilyCompanies, saveProfile, m, PREFIX, countryPrefix, formElement, isSubmitting, validateField, isMaiFamilyLocked }
  },
}

const useLoyaltyProfile = () => {
  const profile = Vue.reactive({
    title: AuthService.memberProfile?.title,
    firstName: AuthService.memberProfile?.firstName,
    lastName: AuthService.memberProfile?.lastName,
    email: AuthService.memberProfile?.email,
    address: AuthService.memberProfile?.address,
    city: AuthService.memberProfile?.city,
    postalCode: AuthService.memberProfile?.postalCode,
    country: AuthService.memberProfile?.country,
    _birthdate: AuthService.memberProfile?.birthdate ? new Date(AuthService.memberProfile?.birthdate.substring(0, 10)) : '',
    get birthdate () {
      if (this._birthdate) {
        return this._birthdate.toISOString().split('T')[0]
      }
      return ''
    },
    set birthdate (date) {
      this._birthdate = new Date(date)
    },
    phonenumber: AuthService.memberProfile?.phonenumber,
    locale: AuthService.memberProfile?.locale,
    isMaiFamily: AuthService.memberProfile?.isMaiFamily,
    corporateCardNumber: AuthService.memberProfile?.corporateCardNumber,
    companyName: AuthService.memberProfile?.companyName,
  })
  return { profile }
}

export { LoyaltyProfile, useLoyaltyProfile }
